import {datadogRum} from '@datadog/browser-rum';
import {useEffect, useRef} from 'react';

export enum LookupSchemaRumAction {
  SCHEMAS_READY = 'lookup_config.schemas_ready',
  UNSUPPORTED = 'lookup_config.unsupported',
  SCHEMA_CACHE_MISS = 'lookup_config.schema_cache_miss',
  CLOSURE_SIZE = 'lookup_config.closure_size',
}

export enum LookupSchemaSurface {
  LAB_AUTOMATION_INPUT_FILE_CONFIG = 'lab_automation_input_file_config',
  LAB_AUTOMATION_MODAL = 'lab_automation_modal',
}

// Every lookup UI reads schemas that a query for every schema of each type preloaded into the Apollo cache.
const SCHEMA_SOURCE_MODE = 'all_schemas';

let activeReadSet: Set<string> | null = null;
const reportedCacheMisses = new Set<string>();
const reportedReadySurfaces = new Set<LookupSchemaSurface>();

/**
 * Records a lookup schema cache read. Reports the first miss per reader in a page load; a list reader misses when
 * it returns an empty list, which also happens when the tenant has no schemas of that type.
 */
export function recordLookupSchemaRead(reader: string, schemaId: string | null, found: boolean): void {
  activeReadSet?.add(schemaId ?? `list:${reader}`);
  if (!found && !reportedCacheMisses.has(reader)) {
    reportedCacheMisses.add(reader);
    datadogRum.addAction(LookupSchemaRumAction.SCHEMA_CACHE_MISS, {reader, mode: SCHEMA_SOURCE_MODE});
  }
}

export interface LookupSchemaReadSet<T> {
  result: T;
  schemaIdCount: number;
  listReadCount: number;
}

/**
 * Runs `fn` and counts the distinct schema IDs and list readers it read from the cache.
 *
 * `baseSchemaIds` seeds the read set with schema IDs the caller already has in hand (e.g. a modal's base
 * schema), since those are never fetched through the lookup schema cache and would otherwise be missing
 * from the closure even though `fn` reasons about them.
 */
export function trackLookupSchemaReads<T>(
  fn: () => T,
  baseSchemaIds: Array<string | null | undefined> = []
): LookupSchemaReadSet<T> {
  const previousReadSet = activeReadSet;
  const readSet = new Set<string>();
  activeReadSet = readSet;
  for (const schemaId of baseSchemaIds) {
    if (schemaId != null) {
      readSet.add(schemaId);
    }
  }
  try {
    const result = fn();
    const listReadCount = [...readSet].filter((key) => key.startsWith('list:')).length;
    return {result, schemaIdCount: readSet.size - listReadCount, listReadCount};
  } finally {
    activeReadSet = previousReadSet;
  }
}

export function reportLookupClosureSize(
  surface: LookupSchemaSurface,
  readSet: LookupSchemaReadSet<unknown>
): void {
  datadogRum.addAction(LookupSchemaRumAction.CLOSURE_SIZE, {
    surface,
    mode: SCHEMA_SOURCE_MODE,
    schemaIdCount: readSet.schemaIdCount,
    listReadCount: readSet.listReadCount,
  });
}

export function reportLookupSchemasReady(surface: LookupSchemaSurface, durationMs: number): void {
  datadogRum.addAction(LookupSchemaRumAction.SCHEMAS_READY, {
    surface,
    mode: SCHEMA_SOURCE_MODE,
    durationMs: Math.round(durationMs),
  });
}

export function reportLookupConfigUnsupported(surface: LookupSchemaSurface): void {
  datadogRum.addAction(LookupSchemaRumAction.UNSUPPORTED, {surface, mode: SCHEMA_SOURCE_MODE});
}

/**
 * Reports how long the first component on `surface` in a page load waited for lookup schemas. Later mounts read
 * the warm cache, so they are not reported.
 */
export function useReportLookupSchemasReadyOnce(surface: LookupSchemaSurface, isReady: boolean): void {
  const mountedAtMs = useRef(performance.now());
  useEffect(() => {
    if (isReady && !reportedReadySurfaces.has(surface)) {
      reportedReadySurfaces.add(surface);
      reportLookupSchemasReady(surface, performance.now() - mountedAtMs.current);
    }
  }, [isReady, surface]);
}

export function resetLookupSchemaRumForTests(): void {
  activeReadSet = null;
  reportedCacheMisses.clear();
  reportedReadySurfaces.clear();
}
